// src/data/salesOrderPushes.ts
// ---------------------------
// Repository for `request_sales_order_pushes` (db-63) — the per-order Unleashed
// push/queue lifecycle (one row per order_number). The Order Builder inserts a
// `queued` row; the (future) push worker updates it. Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";
import { getConfigValue } from "./portalConfig";

// Per-channel push-alert toggles (portal_config key `unleashed_push_alerts`). Each
// channel defaults ON when the key or a field is absent.
export interface PushAlertFlags {
  email: boolean;
  banner: boolean;
  audit: boolean;
  runLog: boolean;
}

export const PUSH_ALERTS_KEY = "unleashed_push_alerts";

// Normalise a stored value into the four flags — anything not explicitly `false`
// is ON (so a missing key / field defaults to enabled).
export function normalizePushAlerts(value: unknown): PushAlertFlags {
  const v = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const on = (k: string) => v[k] !== false;
  return { email: on("email"), banner: on("banner"), audit: on("audit"), runLog: on("runLog") };
}

// The current push-alert flags (all ON by default). Used by the banner (banner
// flag) and the System Setup editor.
export async function getPushAlertFlags(): Promise<PushAlertFlags> {
  return normalizePushAlerts(await getConfigValue(PUSH_ALERTS_KEY));
}

export type PushRow = Database["public"]["Tables"]["request_sales_order_pushes"]["Row"];
type PushInsert = Database["public"]["Tables"]["request_sales_order_pushes"]["Insert"];
type WriteResult = { error: { message: string } | null };

// The push row for an order_number, or null if none yet. null on error / no client too.
// Read by the top-up picker view (TopUpPickerView) to show this SO's push state.
// (The old comment claimed this guarded against building the same order twice and
// pointed at queueSalesOrderForRequest, which does not call it. Finding B16 item 12.)
export async function getPushByOrderNumber(orderNumber: string): Promise<PushRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("request_sales_order_pushes")
    .select("*")
    .eq("order_number", orderNumber)
    .maybeSingle();
  if (error) {
    logger.warn("getPushByOrderNumber failed:", error.message);
    return null;
  }
  return (data as unknown as PushRow) ?? null;
}

// How many orders (push rows) already exist for a request — used to number the
// next per-session order SO-<ref>-<seq>. 0 on error / no client.
export async function countOrdersForRequest(requestId: number): Promise<number> {
  const c = getTypedClient();
  if (!c) return 0;
  const { count, error } = await c
    .from("request_sales_order_pushes")
    .select("id", { count: "exact", head: true })
    .eq("request_id", requestId);
  if (error) {
    logger.warn("countOrdersForRequest failed:", error.message);
    return 0;
  }
  return count ?? 0;
}

// All push rows (small table — one per order). Powers the Sales Orders viewer's
// status column. null on error / no client.
export async function listPushes(): Promise<PushRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c.from("request_sales_order_pushes").select("*").limit(5000);
  if (error) {
    logger.warn("listPushes failed:", error.message);
    return null;
  }
  return (data as unknown as PushRow[]) ?? [];
}

// Existing SO count per suffix for a request (prior sessions), so the engine
// numbers the next one correctly (…-SCH, …-SCH2). {} on error / no client.
export async function suffixCountsForRequest(requestId: number): Promise<Record<string, number>> {
  const c = getTypedClient();
  if (!c) return {};
  const { data, error } = await c
    .from("request_sales_order_pushes")
    .select("suffix")
    .eq("request_id", requestId)
    .limit(5000);
  if (error) {
    logger.warn("suffixCountsForRequest failed:", error.message);
    return {};
  }
  const counts: Record<string, number> = {};
  for (const r of (data as unknown as Array<{ suffix: string | null }>) ?? []) {
    const s = r.suffix ?? "";
    if (s) counts[s] = (counts[s] ?? 0) + 1;
  }
  return counts;
}

// Bulk-set `api_excluded` (db-70) for the given orders — "hold from API push" /
// "release to API". Reversible. No-op on empty input / no client. Returns { error }.
export async function setApiExcludedForOrders(
  orderNumbers: string[],
  excluded: boolean
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  if (orderNumbers.length === 0) return { error: null };
  const { error } = await c
    .from("request_sales_order_pushes")
    .update({ api_excluded: excluded })
    .in("order_number", orderNumbers);
  if (error) logger.warn("setApiExcludedForOrders failed:", error.message);
  return { error };
}

// Bulk-set `exported_at` (db-70) for the given orders. Pass an ISO timestamp to mark
// them exported, or null to allow re-export. No-op on empty input. Returns { error }.
export async function setExportedForOrders(
  orderNumbers: string[],
  when: string | null
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  if (orderNumbers.length === 0) return { error: null };
  const { error } = await c
    .from("request_sales_order_pushes")
    .update({ exported_at: when })
    .in("order_number", orderNumbers);
  if (error) logger.warn("setExportedForOrders failed:", error.message);
  return { error };
}

// Count the orders the push worker WOULD pick up right now: queued or retry_pending
// and NOT api_excluded. Used by the API on/off switch to tell the admin how many
// orders exist at cutover. 0 on error / no client.
export async function countPushableOrders(): Promise<number> {
  const c = getTypedClient();
  if (!c) return 0;
  const { count, error } = await c
    .from("request_sales_order_pushes")
    .select("id", { count: "exact", head: true })
    .in("status", ["queued", "retry_pending"])
    .eq("api_excluded", false);
  if (error) {
    logger.warn("countPushableOrders failed:", error.message);
    return 0;
  }
  return count ?? 0;
}

// Count the orders that PERMANENTLY failed to push (status 'failed') and are not
// held from the API (api_excluded=false). Powers the supervisor+ failed-push
// banner — excluding a failed order acknowledges it and clears it from the count.
// 0 on error / no client.
export async function countFailedPushes(): Promise<number> {
  const c = getTypedClient();
  if (!c) return 0;
  const { count, error } = await c
    .from("request_sales_order_pushes")
    .select("id", { count: "exact", head: true })
    .eq("status", "failed")
    .eq("api_excluded", false);
  if (error) {
    logger.warn("countFailedPushes failed:", error.message);
    return 0;
  }
  return count ?? 0;
}

// Exclude EVERY currently-pushable order from the API (the "exclude existing on
// cutover" option of the switch). Idempotent. Returns { error }.
export async function excludeAllPushableOrders(): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("request_sales_order_pushes")
    .update({ api_excluded: true })
    .in("status", ["queued", "retry_pending"])
    .eq("api_excluded", false);
  if (error) logger.warn("excludeAllPushableOrders failed:", error.message);
  return { error };
}

// Re-queue ONE order for the push worker (the Sales Orders screen's Retry button):
// reset it to 'queued' and clear the retry/error state so the next worker run
// attempts it fresh — the UI equivalent of the manual SQL reset. Un-excludes it too
// so the retry actually runs. Returns { error }.
//
// ONLY 'failed' / 'retry_pending' rows may be re-queued. The worker mints a FRESH GUID
// per attempt (push-sales-orders POSTs to SalesOrders/{guid}), so re-queuing an order
// Unleashed already accepted creates a SECOND sales order there. This guard used to be
// `.neq("status","hold")` — i.e. a 'pushed' row could be re-queued — with the only real
// protection being the `canRetry` boolean in SalesOrdersAdmin. That was one UI condition
// away from a duplicate order. The guard belongs here, below the UI.
// Finding B16 item 11, 18 Aug 2026.
//
// `allowStuckPushing` is the deliberate escape hatch (John, 23 Aug). A worker run that
// dies mid-POST leaves a row stranded at 'pushing' forever, and the strict guard above
// made that unrecoverable from the UI. The caller may opt in to re-queue such a row —
// but ONLY behind a confirm dialog, because if that POST did reach Unleashed this
// creates a duplicate order. It stays opt-in so the safe path is the default and the
// risky one has to be asked for by name.
export async function requeuePush(
  orderNumber: string,
  opts?: { allowStuckPushing?: boolean }
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("request_sales_order_pushes")
    .update({
      status: "queued",
      attempt_count: 0,
      next_attempt_at: null,
      error_message: null,
      api_excluded: false,
    })
    .eq("order_number", orderNumber)
    .in(
      "status",
      opts?.allowStuckPushing ? ["failed", "retry_pending", "pushing"] : ["failed", "retry_pending"]
    );
  if (error) logger.warn("requeuePush failed:", error.message);
  return { error };
}

// Insert a fresh push row for an order (with its suffix/sequence/payee). Status
// defaults to 'queued'; pass status:'hold' (db-113) to park a picker-processed
// DEV/STA order out of the worker's reach until the picker releases it. Logs on
// error and returns { error }.
export async function createQueuedPush(
  orderNumber: string,
  requestId: number,
  extra?: {
    suffix?: string;
    suffix_sequence?: number;
    payee_identifier?: string | null;
    status?: string;
  }
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const row = {
    order_number: orderNumber,
    request_id: requestId,
    status: extra?.status ?? "queued",
    suffix: extra?.suffix ?? null,
    suffix_sequence: extra?.suffix_sequence ?? null,
    payee_identifier: extra?.payee_identifier ?? null,
  };
  const { error } = await c.from("request_sales_order_pushes").insert(row as PushInsert);
  if (error) logger.warn("createQueuedPush failed:", error.message);
  return { error };
}

// Release a request's held picker orders (status='hold', db-113) back to 'queued'
// so the push worker can pick them up once the picker has finished. Only affects
// 'hold' rows for that request; other statuses are untouched. Returns { error }.
export async function releaseHeldPushesForRequest(requestId: number): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("request_sales_order_pushes")
    .update({ status: "queued" })
    .eq("request_id", requestId)
    .eq("status", "hold");
  if (error) logger.warn("releaseHeldPushesForRequest failed:", error.message);
  return { error };
}

// Release ONE held picker order (status='hold', db-113) back to 'queued' by its SO /
// order number — option (b): the picker view releases only the SO it just processed,
// leaving any other held SO on the same request untouched. Returns { error }.
export async function releaseHeldPushByOrderNumber(orderNumber: string): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("request_sales_order_pushes")
    .update({ status: "queued" })
    .eq("order_number", orderNumber)
    .eq("status", "hold");
  if (error) logger.warn("releaseHeldPushByOrderNumber failed:", error.message);
  return { error };
}

// Does this request have any held picker orders (status='hold') still to release?
// Used by the picker view to know whether there is anything to send. false on
// error / no client.
export async function hasHeldPushesForRequest(requestId: number): Promise<boolean> {
  const c = getTypedClient();
  if (!c) return false;
  const { count, error } = await c
    .from("request_sales_order_pushes")
    .select("id", { count: "exact", head: true })
    .eq("request_id", requestId)
    .eq("status", "hold");
  if (error) {
    logger.warn("hasHeldPushesForRequest failed:", error.message);
    return false;
  }
  return (count ?? 0) > 0;
}
